import React, { useState, useEffect } from 'react';
import { User, Settings, Phone, Building2, Briefcase, Lock, Save, Eye, EyeOff, Users } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { apiGet, apiPatch, apiPost } from '@/services/api';
import { Button, Card, LoadingState } from '@/components/common';

interface UserProfile {
  id: number;
  login: string;
  firstName: string;
  lastName: string;
  email: string;
  admin: boolean;
  groups: string[];
  customFields: {
    functie: string;
    departament: string;
    telefon: string;
  };
}

export function SettingsPage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state pentru editare profil
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [functie, setFunctie] = useState('');
  const [departament, setDepartament] = useState('');
  const [telefon, setTelefon] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Form state pentru schimbare parolă
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  const loadProfile = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await apiGet<UserProfile>('/auth/data/me/profile');
      setProfile(data);
      setFirstName(data.firstName || '');
      setLastName(data.lastName || '');
      setFunctie(data.customFields?.functie || '');
      setDepartament(data.customFields?.departament || '');
      setTelefon(data.customFields?.telefon || '');
    } catch (err) {
      console.error('Failed to load profile:', err);
      setError('Eroare la încărcarea profilului');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await apiPatch('/auth/data/me/profile', {
        firstname: firstName,
        lastname: lastName,
        functie,
        departament,
        telefon,
      });
      await loadProfile();
      setSuccessMessage('Profilul a fost actualizat cu succes!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError('Eroare la salvarea profilului');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);

    // Validări
    if (!currentPassword) {
      setPasswordError('Introduceți parola curentă');
      return;
    }
    if (!newPassword) {
      setPasswordError('Introduceți noua parolă');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Noua parolă trebuie să aibă minim 8 caractere');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Parolele nu coincid');
      return;
    }

    setIsSavingPassword(true);

    try {
      await apiPost('/auth/data/me/password', {
        currentPassword,
        newPassword,
      });
      setPasswordSuccess('Parola a fost schimbată cu succes!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to change password:', err);
      setPasswordError(err.message || 'Eroare la schimbarea parolei');
    } finally {
      setIsSavingPassword(false);
    }
  };

  if (isLoading) {
    return <LoadingState message="Se încarcă setările..." />;
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-lg bg-primary-500 text-white">
          <Settings className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Setări cont</h1>
          <p className="text-gray-600 mt-1">Gestionează informațiile contului tău</p>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {successMessage}
        </div>
      )}

      {/* Informații cont (doar citire) */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-gray-500" />
          Informații cont
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-500">Utilizator</label>
            <p className="text-gray-900 font-medium">{profile?.login || '-'}</p>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-500">Email</label>
            <p className="text-gray-900">{profile?.email || '-'}</p>
          </div>
          <div className="space-y-1 md:col-span-2">
            <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
              <Users className="w-4 h-4" />
              Grup
            </label>
            <p className="text-gray-900">
              {profile?.groups && profile.groups.length > 0
                ? profile.groups.join(', ')
                : <span className="text-gray-400 italic">Fără grup</span>
              }
            </p>
          </div>
        </div>
      </Card>

      {/* Informații profesionale (editabile) */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-gray-500" />
          Date personale și profesionale
        </h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  Prenume
                </span>
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Prenume"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  Nume
                </span>
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Nume"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span className="flex items-center gap-1">
                <Briefcase className="w-4 h-4" />
                Funcție
              </span>
            </label>
            <input
              type="text"
              value={functie}
              onChange={(e) => setFunctie(e.target.value)}
              placeholder="ex: Manager proiect, Inginer, Director..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span className="flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                Departament
              </span>
            </label>
            <input
              type="text"
              value={departament}
              onChange={(e) => setDepartament(e.target.value)}
              placeholder="ex: IT, Resurse Umane, Financiar..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                Telefon
              </span>
            </label>
            <input
              type="tel"
              value={telefon}
              onChange={(e) => setTelefon(e.target.value)}
              placeholder="ex: 0722 123 456"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="pt-2">
            <Button
              onClick={handleSaveProfile}
              isLoading={isSavingProfile}
              leftIcon={<Save className="w-4 h-4" />}
            >
              Salvează modificările
            </Button>
          </div>
        </div>
      </Card>

      {/* Schimbare parolă */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-gray-500" />
          Schimbare parolă
        </h2>

        {passwordError && (
          <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
            {passwordError}
          </div>
        )}

        {passwordSuccess && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            {passwordSuccess}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Parola curentă
            </label>
            <div className="relative">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Parolă nouă
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">Minim 8 caractere</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirmă parola nouă
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="pt-2">
            <Button
              onClick={handleChangePassword}
              isLoading={isSavingPassword}
              variant="secondary"
              leftIcon={<Lock className="w-4 h-4" />}
            >
              Schimbă parola
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
